export interface PerfStateSnapshot {
  dtMs: number;
  visualDtMs?: number;
  pos: { x: number; y: number; z: number };
  cameraPos?: { x: number; y: number; z: number };
  groundY?: number;
  kerbY?: number;
  hoverYOffset?: number;
}

export interface PerfEvent {
  t: number;
  kind: string;
  data?: unknown;
}

export interface PerfFrameSnapshot {
  dtMs: number;
  phases: Record<string, number>;
  state?: PerfTrackedState;
}

export interface PerfTrackedState extends PerfStateSnapshot {
  dPos: number;
  dY: number;
  dCamera: number;
  jerk: number;
}

export interface PhaseStats {
  mean: number;
  p95: number;
  max: number;
}

export interface PerfSnapshot {
  active: boolean;
  frameDtMs: number;
  phaseStats: Record<string, PhaseStats>;
  currentPhases: Record<string, number>;
  uncategorizedMs: number;
  state?: PerfTrackedState;
  events: PerfEvent[];
  frames: PerfFrameSnapshot[];
}

export interface PerfDiagApi {
  mark(phase: string): void;
  end(phase: string): void;
  measure<T>(phase: string, fn: () => T): T;
  frameBoundary(frameDtMs?: number): void;
  event(kind: string, data?: unknown): void;
  state(snapshot: PerfStateSnapshot): void;
  snapshot(): PerfSnapshot;
}

const FRAME_RING_SIZE = 120;
const EVENT_RING_SIZE = 50;
const JERK_THRESHOLD = 500;

const noopSnapshot: PerfSnapshot = {
  active: false,
  frameDtMs: 0,
  phaseStats: {},
  currentPhases: {},
  uncategorizedMs: 0,
  events: [],
  frames: [],
};

const noopApi: PerfDiagApi = {
  mark: () => {},
  end: () => {},
  measure: (_phase, fn) => fn(),
  frameBoundary: () => {},
  event: () => {},
  state: () => {},
  snapshot: () => noopSnapshot,
};

export let perf: PerfDiagApi = noopApi;

export function installPerfDiag(): PerfDiagApi {
  const runtime = new PerfDiagRuntime();
  perf = runtime;
  return runtime;
}

export function uninstallPerfDiag(): void {
  perf = noopApi;
}

class PerfDiagRuntime implements PerfDiagApi {
  private readonly frames: PerfFrameSnapshot[] = [];
  private readonly events: PerfEvent[] = [];
  private readonly phaseStarts = new Map<string, number[]>();
  private currentPhases: Record<string, number> = {};
  private currentFrameDtMs = 0;
  private currentState: PerfTrackedState | undefined;
  private previousState: PerfTrackedState | undefined;
  private previousVelocity: { x: number; y: number; z: number } | undefined;
  private previousAcceleration: { x: number; y: number; z: number } | undefined;

  mark(phase: string): void {
    const stack = this.phaseStarts.get(phase) ?? [];
    stack.push(performance.now());
    this.phaseStarts.set(phase, stack);
  }

  end(phase: string): void {
    const stack = this.phaseStarts.get(phase);
    const start = stack?.pop();
    if (typeof start !== 'number') return;
    this.currentPhases[phase] = (this.currentPhases[phase] ?? 0) + performance.now() - start;
  }

  measure<T>(phase: string, fn: () => T): T {
    this.mark(phase);
    try {
      return fn();
    } finally {
      this.end(phase);
    }
  }

  frameBoundary(frameDtMs = 0): void {
    if (this.currentFrameDtMs > 0 || Object.keys(this.currentPhases).length > 0 || this.currentState) {
      pushRing(this.frames, {
        dtMs: this.currentFrameDtMs,
        phases: { ...this.currentPhases },
        state: this.currentState,
      }, FRAME_RING_SIZE);
      this.detectFrameEvents();
    }
    this.currentFrameDtMs = frameDtMs;
    this.currentPhases = {};
    this.phaseStarts.clear();
  }

  event(kind: string, data?: unknown): void {
    pushRing(this.events, { t: performance.now(), kind, data }, EVENT_RING_SIZE);
  }

  state(snapshot: PerfStateSnapshot): void {
    const prev = this.previousState;
    const dtSec = Math.max(0.001, snapshot.dtMs / 1000);
    const velocity = prev
      ? vectorScale(vectorSub(snapshot.pos, prev.pos), 1 / dtSec)
      : { x: 0, y: 0, z: 0 };
    const acceleration = this.previousVelocity
      ? vectorScale(vectorSub(velocity, this.previousVelocity), 1 / dtSec)
      : { x: 0, y: 0, z: 0 };
    const jerkVector = this.previousAcceleration
      ? vectorScale(vectorSub(acceleration, this.previousAcceleration), 1 / dtSec)
      : { x: 0, y: 0, z: 0 };
    const dCamera = prev?.cameraPos && snapshot.cameraPos
      ? vectorLength(vectorSub(snapshot.cameraPos, prev.cameraPos))
      : 0;
    const tracked: PerfTrackedState = {
      ...snapshot,
      dPos: prev ? vectorLength(vectorSub(snapshot.pos, prev.pos)) : 0,
      dY: prev ? snapshot.pos.y - prev.pos.y : 0,
      dCamera,
      jerk: vectorLength(jerkVector),
    };
    this.currentState = tracked;
    if (
      typeof snapshot.kerbY === 'number'
      && typeof prev?.kerbY === 'number'
      && Math.abs(snapshot.kerbY - prev.kerbY) > 0.05
    ) {
      this.event('yDiscontinuity', {
        from: prev.kerbY,
        to: snapshot.kerbY,
        delta: snapshot.kerbY - prev.kerbY,
      });
    }
    if (tracked.jerk > JERK_THRESHOLD) {
      this.event('jerkSpike', { jerk: tracked.jerk, dY: tracked.dY, dCamera });
    }
    this.previousState = tracked;
    this.previousVelocity = velocity;
    this.previousAcceleration = acceleration;
  }

  snapshot(): PerfSnapshot {
    const currentPhases = { ...this.currentPhases };
    const sum = sumValues(currentPhases);
    return {
      active: true,
      frameDtMs: this.currentFrameDtMs,
      phaseStats: this.phaseStats(),
      currentPhases,
      uncategorizedMs: Math.max(0, this.currentFrameDtMs - sum),
      state: this.currentState,
      events: [...this.events].reverse(),
      frames: [...this.frames],
    };
  }

  private phaseStats(): Record<string, PhaseStats> {
    const values = new Map<string, number[]>();
    for (const frame of this.frames) {
      for (const [phase, ms] of Object.entries(frame.phases)) {
        const arr = values.get(phase) ?? [];
        arr.push(ms);
        values.set(phase, arr);
      }
    }
    for (const [phase, ms] of Object.entries(this.currentPhases)) {
      const arr = values.get(phase) ?? [];
      arr.push(ms);
      values.set(phase, arr);
    }
    const stats: Record<string, PhaseStats> = {};
    for (const [phase, arr] of values) {
      const sorted = [...arr].sort((a, b) => a - b);
      const mean = sumArray(arr) / arr.length;
      const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] ?? 0;
      const max = sorted[sorted.length - 1] ?? 0;
      stats[phase] = { mean, p95, max };
    }
    return stats;
  }

  private detectFrameEvents(): void {
    const phases = this.currentPhases;
    const frameDtMs = this.currentFrameDtMs;
    if (frameDtMs <= 0) return;
    if (frameDtMs > 25) {
      let dominantPhase = 'uncategorized';
      let dominantMs = Math.max(0, frameDtMs - sumValues(phases));
      for (const [phase, ms] of Object.entries(phases)) {
        if (ms > dominantMs) {
          dominantPhase = phase;
          dominantMs = ms;
        }
      }
      this.event('slowFrame', { frameDtMs, dominantPhase, dominantMs });
    }
    const recent = this.frames.slice(-30).map((frame) => frame.dtMs).filter((dt) => dt > 0);
    if (recent.length > 5) {
      const meanDt = sumArray(recent) / recent.length;
      if (frameDtMs > meanDt * 2) this.event('dtSpike', { frameDtMs, meanDt });
    }
    if (frameDtMs > 50 && Object.values(phases).every((ms) => ms <= 5)) {
      this.event('gcSuspect', { frameDtMs });
    }
  }
}

function pushRing<T>(arr: T[], item: T, max: number): void {
  arr.push(item);
  if (arr.length > max) arr.shift();
}

function sumValues(values: Record<string, number>): number {
  return sumArray(Object.values(values));
}

function sumArray(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0);
}

function vectorSub(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function vectorScale(
  v: { x: number; y: number; z: number },
  scale: number,
): { x: number; y: number; z: number } {
  return { x: v.x * scale, y: v.y * scale, z: v.z * scale };
}

function vectorLength(v: { x: number; y: number; z: number }): number {
  return Math.hypot(v.x, v.y, v.z);
}
